'use client';

import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';

export function HowItWorks() {
  const { locale } = useLocale();

  return (
    <section id="how" className="relative py-14 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-4">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.how.eyebrow, locale)}
          </div>
          <h2 className="font-serif text-display-sm text-cream leading-[0.98]">
            {t(dict.how.heading, locale)}
          </h2>
        </div>

        <div className="relative grid md:grid-cols-3 gap-8">
          {/* Connecting line (decorative) */}
          <div
            aria-hidden
            className="hidden md:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"
          />

          {dict.how.steps.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative"
            >
              {/* Soft halo behind the numeral — fades in on hover so the
                  whole step lights up rather than just the disc. */}
              <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 top-0 w-32 h-32 rounded-full bg-gold/10 blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 ease-out"
              />
              <div className="relative z-10 w-20 h-20 rounded-full bg-ink-800 border border-gold/30 grid place-items-center font-serif text-3xl text-gold-soft tnum mb-6 mx-auto transition-[border-color,box-shadow,transform] duration-300 ease-out group-hover:border-gold/60 group-hover:shadow-glow-gold group-hover:scale-[1.03]">
                {step.n}
              </div>
              <h3 className="font-serif text-xl text-fog-100 mb-3 text-center">
                {t(step.title, locale)}
              </h3>
              <p className="text-fog-300 text-[15px] leading-relaxed text-center max-w-xs mx-auto">
                {t(step.body, locale)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
